import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { helper } from 'src/environments/helper';
import { OperatoriInstitutiiService } from '../operatori-institutii.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { OperatorAddPayload } from '../operatori-institutii.interface';
import { Observable, Subscription, catchError, distinctUntilChanged, finalize, map, of, switchMap, tap } from 'rxjs';
import { CompartimenteService } from '../../../settings-sidenav/compartimente/compartimente.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { UsersService } from '../../../settings-sidenav/users-settings/users.service';
import { notDefaultValidator } from 'src/app/shared/validators/not-default';

@Component({
    selector: 'app-add-operatori-institutii',
    templateUrl: './add-operatori-institutii.component.html',
    styleUrls: ['./add-operatori-institutii.component.scss']
})
export class AddOperatoriInstitutiiComponent implements OnInit, OnDestroy {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    token!: string;
    isLoading = false;
    form: FormGroup = new FormGroup({
        name: new FormControl('', [Validators.required]),
        type: new FormControl('-1', [Validators.required, notDefaultValidator('-1')]),
        id_directie: new FormControl('-1', [Validators.required, notDefaultValidator('-1')]),
        id_compartiment: new FormControl('-1'),
        id_user: new FormControl(''),
        user: new FormControl(''),
        observations: new FormControl(''),
    });

    directiiActive!: Generic[];
    compartimenteActive$!: Observable<Generic[]>;
    userTypes!: Generic[];
    usersActive$: Generic[] = [];

    filteredActiveUsers!: any[];
    activeUsersChangesSubscription = new Subscription();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialogRef: MatDialogRef<AddOperatoriInstitutiiComponent>,
        private operatoriInstitutiiService: OperatoriInstitutiiService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private compartimenteService: CompartimenteService,
        private usersService: UsersService
    ) {
        this.processToken();
        this.directiiActive = this.data.directiiActive;
        this.userTypes = this.data.userTypes;
    }

    ngOnInit(): void {
        this.renderActiveUsersSearch();
    }

    ngOnDestroy(): void {
        if (this.activeUsersChangesSubscription) {
            this.activeUsersChangesSubscription.unsubscribe();
        }
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.operatoriInstitutiiService.setToken(token);
        });
    }

    submitForm() {
        this.isLoading = true;

        const { name, type, id_directie, id_compartiment, id_user, observations } = this.form
            .value as OperatorAddPayload;

        const newOperator: OperatorAddPayload = {
            name,
            type,
            id_directie,
            id_compartiment,
            id_user,
            observations,
        };

        this.operatoriInstitutiiService
            .storeOperator(newOperator)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['id_compartiment'].patchValue('-1');
        this.form.controls['user'].patchValue('');
        this.form.controls['id_user'].patchValue('-1');

        if (directieId == -1) {
            this.compartimenteActive$ = of([]);
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
            this.loadUsersActive();
        } else {
            this.compartimenteActive$ = new Observable<Generic[]>((observer) => {
                observer.complete();
            });
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.compartimenteService.getCompartimenteByDirectieId(directieId)
            : this.compartimenteService.getCompartimenteActive();

        this.compartimenteActive$ = compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isLoading = false;
            })
        );
    }

    onDirectieCompartimentChange(compartimentId: number): void {
        this.loadUsersActive();
    }

    loadUsersActive() {
        let data = {
            directie_id: this.form.get('id_directie')?.value,
            compartiment_id: this.form.get('id_compartiment')?.value === '-1' ? '' : this.form.get('id_compartiment')?.value,
        }

        this.isLoading = true;
        this.usersService
            .getActiveUsersByOrganigrama(data)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => (this.isLoading = false))
            ).subscribe({
                next: (res) => {
                    this.usersActive$ = res;
                    this.filteredActiveUsers = res;
                    console.log('usersActive$', this.usersActive$);
                }
            });
    }

    renderActiveUsersSearch() {
        this.activeUsersChangesSubscription = this.form.controls['user'].valueChanges.pipe(
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredActiveUsers = this.usersActive$;
                    return of(this.usersActive$);
                }
                return this.filterActiveUsers(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredActiveUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    filterActiveUsers(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.usersActive$.filter(user =>
            this.normalizeString(user.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    onActiveUsersChange(user: { id: number, name: string }) {

        if (this.activeUsersChangesSubscription) {
            this.activeUsersChangesSubscription.unsubscribe();
        }

        this.form.controls['id_user'].setValue(user.id);

        this.renderActiveUsersSearch();
    }
}
